import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { withTranslation } from '@application/i18n/with_translation';
import { UnitNode, UnitService } from '@application/unit.service';
import { ActionCard, Flex, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Shimmer } from '@component/new_shimmer.component';
import { AsyncSignal, AsyncSignalValue, asyncSignalAccessible, asyncSignalIdle } from '@cortex-shim/async_signal.component';
import { RouterService } from '@cortex-shim/router.service';
import { useRepresentablePatients } from '@feature/representation/use-representable-patients';
import { signal } from '@preact/signals-react';
import { Injectable, Scopes, useResolve, useResolveController, withNewContainer } from '@visiba/instantiation';
import { useViewFindReceptionTempNavigationContext } from './_temp_navigation.context';

export const ListedAtReceptions = withNewContainer(function ListedAtReceptions() {
	const controller = useResolveController(Controller);
	const routerService = useResolve(RouterService);
	const { __TEMP__onNavigate } = useViewFindReceptionTempNavigationContext();
	const hideListedAtReceptions = useFeatureFlag('PatientWeb.HideListedAtReceptions');
	const { currentlyRepresentedPatient } = useRepresentablePatients({ enabled: useFeatureFlag('PatientWeb.UseRepresentatives') });

	if (hideListedAtReceptions) {
		return null;
	}

	function handleOnReceptionClick(reception: UnitNode) {
		if (__TEMP__onNavigate) {
			__TEMP__onNavigate(`/${reception.routeWithoutLicenceholder}`);
			return;
		}

		routerService.navigateByUrl(`/${reception.routeWithoutLicenceholder}`);
	}

	return (
		<AsyncSignal signal={controller.listedAtReceptions} pending={<Skeleton />}>
			{(listedAtReceptions) => {
				if (listedAtReceptions.length === 0) {
					return null;
				}

				return (
					<Flex gap={tokens.spacing.x1}>
						<Text variant='labelSemiBold'>
							{currentlyRepresentedPatient?.CommonName
								? withTranslation('View.FindReception.ListedAtReceptions.RepresentedPatientTitle', {
										name: currentlyRepresentedPatient.CommonName,
									})
								: withTranslation('View.FindReception.ListedAtReceptions.Title')}
						</Text>
						<Flex gap={tokens.spacing.x1}>
							{listedAtReceptions.map((reception) => (
								<ActionCard.Root onClick={() => handleOnReceptionClick(reception)} key={reception.id} icon='house'>
									<ActionCard.Title>{reception.name}</ActionCard.Title>
								</ActionCard.Root>
							))}
						</Flex>
					</Flex>
				);
			}}
		</AsyncSignal>
	);
});

function Skeleton() {
	return <Shimmer width='100%' height={tokens.spacing.x8} />;
}

@Injectable({
	scope: Scopes.Scoped,
})
class Controller {
	public listedAtReceptions = signal<AsyncSignalValue<UnitNode[]>>(asyncSignalIdle());

	constructor(private readonly unitService: UnitService) {
		// Empty
	}

	whenMount() {
		this.listedAtReceptions.value = asyncSignalAccessible(
			[...this.unitService.units.values()].filter((unit) => {
				return unit.isEntrance && unit.iAmListedHere;
			}),
		);
	}
}
